import React from 'react'
import { CurrencyFormat, DateFormat, PercentFormat } from '../../../../helpers/formatting'
import ManagerApprovalIcons from '../../../../components/elements/ManagerApprovalIcons'

/**
 * Step 3 of the payment-plan wizard — Send Confirmation. Read-only summary
 * of the schedule generated in Step 2 plus a submit button that POSTs the
 * plan to the server. For settlement plans it also shows the discount /
 * settlement-balance rows. The "Send for Approval" vs "Send Confirmation"
 * label is decided by the agent's discount caps (settlement only).
 *
 * Behaviour-preserving extraction from PaymentPlanPayment.jsx. Parent passes
 * everything as props; no internal state.
 */
export default function PaymentPlanStep3({
  settlementResponseData,
  planSelection,
  strategy,
  paymentPlanPaymentsStep3,
  formBody,
  dropdownOptions,
  saving,
  setStep,
  checkChangesMade,
  handleSubmit,
  onSubmit,
  watchCustomerPromotionCodeId,
  settlementDiscountType,
  discountPercentStep1,
  discountAmountStep1,
  discountData,
}) {
  const renderPaymentRows = () =>
    paymentPlanPaymentsStep3.map((plan, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <tr key={index}>
        <td className={`${formBody.payment_date[index] != plan.payment_date ? "has-text-danger" : ""}`}><DateFormat dateString={plan.payment_date} /></td>
        <td>{dropdownOptions.filter((o) => o.id === settlementResponseData?.payment_method_dropdown_option_id)[0].name}</td>
        <td>{dropdownOptions.filter((o) => o.code_key === 'loan_status_collections')[0].name}</td>
        <td><CurrencyFormat value={plan.principal_amount} showCurrencySign={false} /></td>
        <td><CurrencyFormat value={plan.interest_amount} showCurrencySign={false} /></td>
        <td><CurrencyFormat value={plan.late_fee} showCurrencySign={false} /></td>
        <td><CurrencyFormat value={plan.nsf_fee} showCurrencySign={false} /></td>
        <td><CurrencyFormat value={plan.total_amount} showCurrencySign={false} /></td>
        <td>{dropdownOptions.filter((o) => o.code_key === 'payment_status_scheduled')[0].name}</td>
      </tr>
    ))

  // Same date-vs-max_date_without_manager_approval comparison as PaymentPlanStep2 — the
  // BE's is_required_manager_approval can lag (only set once persisted), so this recomputes
  // the same check FE-side from the plan's own dates as a safety net.
  const latestPaymentDate = paymentPlanPaymentsStep3
    .map((plan) => plan.payment_date)
    .filter(Boolean)
    .reduce((latest, date) => (!latest || date > latest ? date : latest), null)
  const isOverMaxDate =
    !watchCustomerPromotionCodeId?.value
    && !!strategy?.requiresManagerApproval
    && !!latestPaymentDate
    && !!settlementResponseData?.max_date_without_manager_approval
    && latestPaymentDate > settlementResponseData.max_date_without_manager_approval

  const isDurationOverLimit = !!settlementResponseData?.is_required_manager_approval || isOverMaxDate

  const showApprovalLabel =
    (!watchCustomerPromotionCodeId?.value && settlementDiscountType === "percent" && discountPercentStep1 > discountData?.max_discount_percent?.agent)
    || (settlementDiscountType === "amount" && discountAmountStep1 > discountData?.max_discount_amount?.agent)
    || isDurationOverLimit

  return (
    <form onSubmit={handleSubmit(onSubmit)} id="payment_plan_payment_step3_form">
      <p className="title is-5" data-testid="step3-heading">
        Payment Plan - {strategy?.stepTitle} - Step 3 {isOverMaxDate && (
          <span data-testid="step3-duration-approval-notice">
            <ManagerApprovalIcons type="mgr_approval_status_required" />
            <span className="is-size-6 has-text-danger"> Plan completes after <DateFormat dateString={settlementResponseData.max_date_without_manager_approval} /></span>
          </span>
        )}
      </p>
      <table className="table is-narrow table-payment-schedule-panel">
        <tbody>
          <tr data-testid="step3-balance-row-outstanding">
            <td><b>Outstanding Balance</b></td>
            <td><CurrencyFormat value={settlementResponseData?.outstanding_total} /></td>
            <td data-testid="step3-discount-header"><b>{strategy?.includesDiscount ? "Discount" : ""}</b></td>
            <td>{strategy?.includesDiscount ? <PercentFormat value={settlementResponseData?.discount_percent} /> : ""}</td>
            <td></td>
            <td></td>
          </tr>

          {strategy?.includesDiscount && (
            <tr data-testid="step3-balance-row-settlement">
              <td><b>Settlement Balance</b></td>
              <td><CurrencyFormat value={settlementResponseData?.owed_amount} /></td>
              <td><b>Discount</b></td>
              <td><CurrencyFormat value={settlementResponseData?.discount_amount} /></td>
              <td></td>
              <td></td>
            </tr>
          )}
        </tbody>
      </table>

      <table className="table is-striped is-fullwidth" data-testid="step3-schedule-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Method</th>
            <th>Type</th>
            <th>Principal</th>
            <th>Interest</th>
            <th>Late Fee</th>
            <th>NSF Fee</th>
            <th>Total</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {renderPaymentRows()}
        </tbody>
      </table>

      <table className="table is-narrow table-payment-schedule-panel">
        <tbody>
          <tr>
            <td></td>
            <td colSpan={2}>
              <div className="has-text-right">
                <button
                  type="button"
                  data-testid="step3-cancel"
                  className="button has-background-grey has-text-white is-small mr-1"
                  onClick={() => checkChangesMade()}
                >
                  <span className="mx-3">Cancel</span>
                </button>
                <button
                  type="button"
                  data-testid="step3-back"
                  className="button has-background-grey has-text-white is-small mr-1"
                  onClick={() => setStep("step2")}
                >
                  <span className="mx-3">Back</span>
                </button>
                <button
                  type="submit"
                  data-testid="step3-send-confirmation"
                  className="button is-link is-small mr-1"
                  form="payment_plan_payment_step3_form"
                  disabled={saving && 'disabled'}
                >
                  {showApprovalLabel ? (
                    <span className="mx-3">{saving ? 'Sending for Approval' : 'Send for Approval'}</span>
                  ) : (
                    <span className="mx-3">{saving ? 'Sending' : 'Send Confirmation'}</span>
                  )}
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </form>
  )
}
